import { Router } from '@vaadin/router';
import '@material/web/tabs/tabs.js';
import '@material/web/tabs/secondary-tab.js';
import '@material/web/textfield/outlined-text-field.js';
import '@material/web/select/outlined-select.js';
import '@material/web/select/select-option.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/text-button.js';

import { api, ApiError, type LedgerEntry, type Member, type Reward, type Tag, type Template } from '../api.js';
import { ce, el, clear, fmtMinutes, avatarEl, type TextFieldEl, type ValueEl } from '../dom.js';
import { getLocale, t, type MessageKey } from '../i18n.js';

// --- Small Material builders, so the cards below read as forms and not as
// custom-element boilerplate. Ids stay where they were: E2E specs address
// these controls by id, and Material keeps the id on the host.

/** A text field. Specs reach the real <input> through `#<id> input`. */
function textField(id: string, label: string, attrs: Record<string, string> = {}): TextFieldEl {
  return ce('md-outlined-text-field', { id, label, ...attrs }) as TextFieldEl;
}

/** A select built from [value, label] pairs; first option selected by default. */
function selectField(id: string, label: string, opts: [string, string][], current?: string): ValueEl {
  const chosen = current ?? opts[0]?.[0] ?? '';
  return ce('md-outlined-select', { id, label }, ...opts.map(([value, text]) =>
    ce('md-select-option', { value, ...(value === chosen ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, text)),
  )) as ValueEl;
}

type BtnKind = 'filled' | 'outlined' | 'text';
function button(kind: BtnKind, label: string, onClick: () => void, attrs: Record<string, string> = {}): HTMLElement {
  // Material buttons default to type="submit": inside a card's <form> an
  // unmarked button would submit it. These never do — they act on click.
  const node = ce(`md-${kind}-button`, { type: 'button', ...attrs }, label);
  node.addEventListener('click', onClick);
  return node;
}

// Admin is four settings sections behind M3 tabs (ADR-0052), not one stack of
// eleven cards. The two "waiting on you" cards (orphaned tasks, pending
// approvals) are pinned above the tabs on EVERY section and render nothing
// when there is nothing to act on, so an admin can't miss them by being on
// the wrong tab.
export type AdminSection = 'people' | 'tasks' | 'ledger' | 'general';

const ADMIN_SECTIONS: { name: AdminSection; label: MessageKey }[] = [
  { name: 'people', label: 'adminSectionPeople' },
  { name: 'tasks', label: 'adminSectionTasks' },
  { name: 'ledger', label: 'adminSectionLedger' },
  { name: 'general', label: 'adminSectionGeneral' },
];

export const ADMIN_SECTION_NAMES: AdminSection[] = ADMIN_SECTIONS.map((s) => s.name);
export const DEFAULT_ADMIN_SECTION: AdminSection = 'people';

/** Which section the current URL (`/admin/<section>`) is on. */
function sectionFromPath(): AdminSection {
  const seg = location.pathname.split('/')[2] ?? '';
  return ADMIN_SECTIONS.find((s) => s.name === seg)?.name ?? DEFAULT_ADMIN_SECTION;
}

type TabsEl = HTMLElement & { activeTabIndex: number };

/**
 * The Admin shell: attention strip + section tabs, appended INTO `host` (the
 * route's own element) rather than into a wrapper of our own. That matters:
 * the section is a NESTED route, so the router appends it to `host` too — and
 * a `position: sticky` tab row can only travel within its parent's box, which
 * has to be the element that spans the section as well. The shell survives
 * tab switches instead of re-fetching its two cards on every tap.
 */
export function renderAdminShell(host: HTMLElement): void {
  const root = host;
  root.classList.add('admin-shell');
  const error = el('p', { class: 'error', id: 'admin-error' });

  const current = sectionFromPath();
  const tabs = ce('md-tabs', { class: 'admin-tabs', 'aria-label': t('adminSectionsLabel') }) as TabsEl;
  tabs.append(
    ...ADMIN_SECTIONS.map(({ name, label }) =>
      ce('md-secondary-tab', { 'data-section': name, ...(name === current ? { active: true } : {}) }, t(label)),
    ),
  );
  // `change` fires for programmatic activation too, so only navigate when the
  // tab and the URL actually disagree — otherwise syncing would loop.
  tabs.addEventListener('change', () => {
    const next = ADMIN_SECTIONS[tabs.activeTabIndex];
    if (next && next.name !== sectionFromPath()) void Router.go(`/admin/${next.name}`);
  });

  // Plain DOM has no disconnect hook: the listener drops itself the first time
  // it fires after the shell has left the page (navigating away from /admin).
  function onLocationChanged(): void {
    if (!root.isConnected) {
      window.removeEventListener('vaadin-router-location-changed', onLocationChanged);
      return;
    }
    tabs.activeTabIndex = ADMIN_SECTIONS.findIndex((s) => s.name === sectionFromPath());
  }
  window.addEventListener('vaadin-router-location-changed', onLocationChanged);

  root.append(error, orphansCard(), approvalsCard(error), tabs);
}

/** One settings section's cards (ADR-0052). */
export function renderAdminSection(section: AdminSection): HTMLElement {
  const root = el('div', { class: 'admin-section', 'data-section': section });
  const error = el('p', { class: 'error', id: 'admin-section-error' });
  root.append(error);
  switch (section) {
    case 'people':
      root.append(membersCard(error), policiesCard(error), accessCard(error));
      break;
    case 'tasks':
      root.append(tagsCard(error), rewardsCard(error));
      break;
    case 'ledger':
      root.append(reversalsCard(error), committedCard(error));
      break;
    case 'general':
      root.append(settingsCard(error));
      break;
  }
  return root;
}

interface FamilySettings {
  weekStartsOn: number;
  wakingMinutesPerDay: number;
  boost: { enabled: boolean; stepPct: number; capMultiplier: number; perHours: number };
  publicAccess: { tokenHash: string | null; version: number };
}

interface AccessStatus {
  enabled: boolean;
  version: number;
  cookieDays: number;
}

function accessCard(error: HTMLElement): HTMLElement {
  const status = el('p', { class: 'muted', id: 'family-access-status' });
  const saved = el('span', { class: 'muted', id: 'family-access-saved' });
  const joinUrl = textField('family-join-url', t('adminAccessLinkLabel'), { readonly: '' });
  const createRotate = ce('md-filled-button', { type: 'button', id: 'family-access-rotate' }, t('adminAccessCreate'));
  const copy = ce('md-outlined-button', { type: 'button', id: 'family-access-copy' }, t('adminCopy'));
  const disable = ce('md-text-button', { type: 'button', class: 'danger', id: 'family-access-disable' }, t('adminAccessDisable'));
  const linkRow = el('div', { class: 'row', id: 'family-access-link-row', style: 'display:none' }, el('div', { class: 'grow' }, joinUrl), copy);

  function showAccess(access: AccessStatus): void {
    status.textContent = access.enabled
      ? t('adminAccessEnabled', { days: access.cookieDays })
      : t('adminAccessDisabled');
    createRotate.textContent = access.enabled ? t('adminAccessRotate') : t('adminAccessCreate');
    disable.style.display = access.enabled ? '' : 'none';
    if (!access.enabled) {
      joinUrl.value = '';
      linkRow.style.display = 'none';
    }
  }

  function load(): void {
    api
      .get<{ access: AccessStatus }>('/api/family/access')
      .then(({ access }) => showAccess(access))
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  createRotate.addEventListener('click', () => {
    error.textContent = '';
    saved.textContent = '';
    api
      .post<{ token: string; access: AccessStatus }>('/api/family/access/rotate')
      .then(({ token, access }) => {
        joinUrl.value = `${location.origin}/join/${token}`;
        linkRow.style.display = '';
        saved.textContent = t('adminAccessLinkReady');
        showAccess(access);
      })
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  });

  copy.addEventListener('click', () => {
    error.textContent = '';
    saved.textContent = '';
    joinUrl.select();
    const write = navigator.clipboard?.writeText(joinUrl.value);
    if (write) {
      void write
        .then(() => {
          saved.textContent = t('adminCopied');
        })
        .catch((err: Error) => {
          error.textContent = err.message;
        });
    } else {
      saved.textContent = t('adminAccessCopyFallback');
    }
  });

  disable.addEventListener('click', () => {
    error.textContent = '';
    saved.textContent = '';
    api
      .del<{ access: AccessStatus }>('/api/family/access')
      .then(({ access }) => {
        saved.textContent = t('adminSaved');
        showAccess(access);
      })
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  });

  load();
  return el(
    'div',
    { class: 'card', id: 'family-access-card' },
    el('h2', {}, t('adminAccessTitle')),
    status,
    linkRow,
    el('div', { class: 'row', style: 'margin-top:10px' }, createRotate, disable, saved),
  );
}

function settingsCard(error: HTMLElement): HTMLElement {
  const weekdays: MessageKey[] = ['weekdayMonday', 'weekdayTuesday', 'weekdayWednesday', 'weekdayThursday', 'weekdayFriday', 'weekdaySaturday', 'weekdaySunday'];
  const weekStart = selectField('setting-weekstart', t('adminWeekStart'), weekdays.map((day, i): [string, string] => [String(i + 1), t(day)]));
  const waking = textField('setting-waking', t('adminWakingHours'), { type: 'number', min: '1', max: '24', step: '0.5' });
  const saved = el('span', { class: 'muted', id: 'settings-saved' });

  api.get<{ family?: { settings: FamilySettings } }>('/api/state').then((state) => {
    const s = state.family?.settings;
    if (!s) return;
    weekStart.value = String(s.weekStartsOn);
    waking.value = String(s.wakingMinutesPerDay / 60);
  });

  return el(
    'div',
    { class: 'card', id: 'settings-card' },
    el('h2', {}, t('adminSettingsTitle')),
    el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          error.textContent = '';
          saved.textContent = '';
          api
            .patch('/api/family/settings', {
              weekStartsOn: Number(weekStart.value),
              wakingMinutesPerDay: Math.round(Number(waking.value) * 60),
            })
            .then(() => {
              saved.textContent = t('adminSaved');
            })
            .catch((err: Error) => {
              error.textContent = err.message;
            });
        },
      },
      el('div', { class: 'admin-form-fields' }, weekStart, waking),
      el('div', { class: 'row', style: 'margin-top:10px' }, ce('md-filled-button', { type: 'submit' }, t('adminSave')), saved),
    ),
  );
}

interface Approval {
  id: number;
  memberId: number;
  action: string;
  payload: Record<string, unknown>;
  status: string;
  createdAt: string;
}

const ACTION_LABELS: Record<string, MessageKey> = {
  'task.create_adhoc': 'actionCreateAdhoc',
  'task.create_recurring': 'actionCreateRecurring',
  'spend.freeform': 'actionSpendFreeform',
  'spend.catalog': 'actionSpendCatalog',
  'committed.log': 'actionCommittedLog',
};

function actionLabel(action: string): string {
  const key = ACTION_LABELS[action];
  return key ? t(key) : action;
}

function describeApproval(a: Approval, memberName: string): string {
  const p = a.payload;
  switch (a.action) {
    case 'task.create_adhoc':
      return t('approvalAddTask', { name: memberName, title: String(p.title), minutes: String(p.baseMinutes) });
    case 'task.create_recurring':
      return t('approvalAddRecurring', {
        name: memberName,
        title: String(p.title),
        minutes: String(p.baseMinutes),
        freq: (p.recurrence as { freq: string }).freq,
      });
    case 'spend.freeform':
      return t('approvalSpend', { name: memberName, minutes: String(p.minutes), note: String(p.note) });
    case 'spend.catalog':
      return t('approvalBuyReward', { name: memberName, id: String(p.rewardId) });
    case 'committed.log':
      return t('approvalCommitted', { name: memberName, minutes: String(p.minutes), date: String(p.date) });
    default:
      return t('approvalFallback', { name: memberName, action: a.action });
  }
}

// Pending approvals (ADR-0052): pinned above the section tabs, so it is
// visible from every section — and hidden entirely when the queue is empty,
// since a permanent "Nothing waiting." card on every screen is just noise.
function approvalsCard(error: HTMLElement): HTMLElement {
  const list = el('div', {});
  const card = el('div', { class: 'card', id: 'approvals-card', style: 'display:none' }, el('h2', {}, t('adminApprovalsTitle')), list);
  let names = new Map<number, string>();

  function load(): void {
    api.get<{ members?: Member[] }>('/api/state').then((state) => {
      names = new Map((state.members ?? []).map((m) => [m.id, m.name]));
      api.get<{ approvals: Approval[] }>('/api/approvals').then(({ approvals }) => {
        clear(list);
        const pending = approvals.filter((a) => a.status === 'pending');
        if (pending.length === 0) {
          card.style.display = 'none';
          return;
        }
        card.style.display = '';
        for (const a of pending) {
          list.append(
            el(
              'div',
              { class: 'task' },
              el('div', { class: 'title' }, describeApproval(a, names.get(a.memberId) ?? `#${a.memberId}`)),
              el(
                'div',
                { class: 'row' },
                button('filled', t('adminApprove'), () => {
                  error.textContent = '';
                  api.post(`/api/approvals/${a.id}/approve`).then(load).catch((err: Error) => {
                    error.textContent = err.message;
                  });
                }),
                button('text', t('adminDeny'), () => {
                  api.post(`/api/approvals/${a.id}/deny`).then(load).catch((err: Error) => {
                    error.textContent = err.message;
                  });
                }, { class: 'danger' }),
              ),
            ),
          );
        }
      });
    });
  }
  load();
  return card;
}

interface Policy {
  id: number;
  action: string;
  targetKind: 'family' | 'tag' | 'member';
  targetId: number | null;
  value: string;
}

function policiesCard(error: HTMLElement): HTMLElement {
  const list = el('div', {});
  const card = el(
    'div',
    { class: 'card', id: 'policies-card' },
    el('h2', {}, t('adminPermissionsTitle')),
    el('p', { class: 'muted' }, t('adminPermissionsHint')),
    list,
  );

  let memberNames = new Map<number, string>();
  let tagNames = new Map<number, string>();

  const action = selectField('policy-action', t('policyActionLabel'),
    Object.entries(ACTION_LABELS).map(([value, label]): [string, string] => [value, t(label)]));
  const target = ce('md-outlined-select', { id: 'policy-target', label: t('policyTargetLabel') }) as ValueEl;
  const value = selectField('policy-value', t('policyValueLabel'), [
    ['allowed', t('policyAllowed')],
    ['approval', t('policyApproval')],
    ['denied', t('policyDenied')],
  ]);

  /** An option for the target select. */
  const targetOption = (val: string, text: string, selected = false): HTMLElement =>
    ce('md-select-option', { value: val, ...(selected ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, text));

  function loadTargets(): void {
    clear(target);
    target.append(targetOption('family:', t('adminEveryone'), true));
    api.get<{ tags: { id: number; name: string; kind: string }[] }>('/api/tags').then(({ tags }) => {
      tagNames = new Map(tags.map((tg) => [tg.id, tg.name]));
      for (const tg of tags.filter((x) => x.kind === 'eligibility')) {
        target.append(targetOption(`tag:${tg.id}`, t('adminGroup', { name: tg.name })));
      }
    });
    api.get<{ members?: Member[] }>('/api/state').then((state) => {
      memberNames = new Map((state.members ?? []).map((m) => [m.id, m.name]));
      for (const m of state.members ?? []) {
        target.append(targetOption(`member:${m.id}`, t('adminMember', { name: m.name })));
      }
      loadPolicies();
    });
  }

  function targetLabel(p: Policy): string {
    if (p.targetKind === 'family') return t('adminEveryone');
    if (p.targetKind === 'tag') return t('adminGroup', { name: String(tagNames.get(p.targetId ?? -1) ?? p.targetId) });
    return t('adminMember', { name: String(memberNames.get(p.targetId ?? -1) ?? p.targetId) });
  }

  function loadPolicies(): void {
    api.get<{ policies: Policy[] }>('/api/policies').then(({ policies }) => {
      clear(list);
      if (policies.length === 0) {
        list.append(el('p', { class: 'muted' }, t('adminNoRules')));
      }
      for (const p of policies) {
        list.append(
          el(
            'div',
            { class: 'task' },
            el('div', {}, `${actionLabel(p.action)} · ${targetLabel(p)} → `, el('strong', {}, t(p.value === 'allowed' ? 'policyAllowed' : p.value === 'approval' ? 'policyApproval' : 'policyDenied'))),
            button('text', t('adminRemove'), () => {
              api.del(`/api/policies/${p.id}`).then(loadPolicies).catch((err: Error) => {
                error.textContent = err.message;
              });
            }),
          ),
        );
      }
    });
  }

  card.append(
    el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          error.textContent = '';
          const [kind, id] = target.value.split(':');
          const body: Record<string, unknown> = {
            action: action.value,
            targetKind: kind,
            value: value.value,
          };
          if (id) body.targetId = Number(id);
          api
            .put('/api/policies', body)
            .then(loadPolicies)
            .catch((err: Error) => {
              error.textContent = err.message;
            });
        },
      },
      el('div', { class: 'admin-form-fields' }, action, target, value),
      el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('adminSetRule'))),
    ),
  );

  loadTargets();
  return card;
}

function membersCard(error: HTMLElement): HTMLElement {
  const list = el('div', {});
  const card = el('div', { class: 'card' }, el('h2', {}, t('adminMembersTitle')), list);

  function load(): void {
    api.get<{ members?: Member[]; setup: boolean }>('/api/state').then((state) => {
      clear(list);
      for (const m of state.members ?? []) {
        list.append(
          el(
            'div',
            { class: 'task member-row' },
            el(
              'div',
              { class: 'row member-identity' },
              avatarEl(m.name, m.avatar, m.color, 28),
              el('span', {}, `${m.name} `),
              m.role === 'admin' ? el('span', { class: 'pill' }, t('pillAdmin')) : null,
            ),
            el(
              'div',
              { class: 'row member-actions' },
              m.role === 'member'
                ? button('text', t('adminMakeAdmin'), () => patch(m.id, { role: 'admin' }))
                : button('text', t('adminDemote'), () => patch(m.id, { role: 'member' })),
              m.hasPin ? button('text', t('adminClearPin'), () => patch(m.id, { pin: null })) : null,
              button('text', t('adminRemove'), () => deactivate(m.id, m.name), { class: 'danger' }),
            ),
          ),
        );
      }
    });
  }

  function patch(id: number, body: unknown): void {
    error.textContent = '';
    api
      .patch(`/api/members/${id}`, body)
      .then(load)
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  // Deactivating a member is guarded (ADR-0043): if it would strand active
  // tasks the server refuses with a `would-orphan` payload; we warn with the
  // task titles and offer to proceed anyway (force).
  function deactivate(id: number, name: string): void {
    error.textContent = '';
    api
      .patch(`/api/members/${id}`, { active: false })
      .then(load)
      .catch((err: Error) => {
        if (err instanceof ApiError && err.status === 409 && err.body.code === 'would-orphan') {
          const titles = (err.body.templates as { title: string }[]).map((tpl) => tpl.title).join(', ');
          if (confirm(`${t('deactivateOrphanWarning', { name, titles })}\n\n${t('deactivateAnyway')}?`)) {
            patch(id, { active: false, force: true });
          }
          return;
        }
        error.textContent = err.message;
      });
  }

  const name = textField('new-member-name', t('adminNamePlaceholder'));
  const avatar = textField('new-member-avatar', t('adminAvatarPlaceholder'));
  card.append(
    el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          error.textContent = '';
          const body: Record<string, string> = { name: name.value.trim() };
          if (avatar.value.trim()) body.avatar = avatar.value.trim();
          api
            .post('/api/members', body)
            .then(() => {
              name.value = '';
              load();
            })
            .catch((err: Error) => {
              error.textContent = err.message;
            });
        },
      },
      el('div', { class: 'admin-form-fields' }, name, avatar),
      el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('adminAddMember'))),
    ),
  );
  load();
  return card;
}

// Orphaned-task warning list (ADR-0043): active recurring tasks no active
// member can do. The card stays empty (renders nothing) when all is well.
function orphansCard(): HTMLElement {
  const card = el('div', { class: 'card warning', id: 'orphans-card', style: 'display:none' });

  function load(): void {
    api
      .get<{ orphans: Template[] }>('/api/templates/orphans')
      .then(({ orphans }) => {
        clear(card);
        if (orphans.length === 0) {
          card.style.display = 'none';
          return;
        }
        card.style.display = '';
        card.append(el('h2', {}, t('adminOrphansTitle')), el('p', { class: 'muted' }, t('adminOrphansIntro')));
        for (const tpl of orphans) {
          card.append(
            el(
              'div',
              { class: 'task' },
              el(
                'div',
                {},
                el('div', { class: 'title' }, tpl.title),
                el('div', { class: 'meta' }, t('adminOrphanReason')),
              ),
              button('text', t('adminRetire'), () => {
                void api.patch(`/api/templates/${tpl.id}`, { active: false }).then(load);
              }, { class: 'danger' }),
            ),
          );
        }
      })
      .catch(() => {
        // Non-admins get 403; nothing to show.
        card.style.display = 'none';
      });
  }

  load();
  return card;
}

function rewardsCard(error: HTMLElement): HTMLElement {
  const list = el('div', {});
  const card = el('div', { class: 'card' }, el('h2', {}, t('adminRewardsTitle')), list);

  function load(): void {
    api.get<{ rewards: Reward[] }>('/api/rewards').then(({ rewards }) => {
      clear(list);
      for (const r of rewards.filter((x) => x.active)) {
        list.append(
          el(
            'div',
            { class: 'task' },
            el('div', {}, el('div', { class: 'title' }, r.title), el('div', { class: 'meta' }, fmtMinutes(r.costMinutes))),
            button('text', t('adminRetire'), () => {
              void api.patch(`/api/rewards/${r.id}`, { active: false }).then(load);
            }, { class: 'danger' }),
          ),
        );
      }
    });
  }

  const title = textField('new-reward-title', t('adminRewardPlaceholder'));
  const cost = textField('new-reward-cost', t('adminRewardCost'), { type: 'number', min: '1', 'suffix-text': t('composeMinSuffix') });
  card.append(
    el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          error.textContent = '';
          api
            .post('/api/rewards', { title: title.value.trim(), costMinutes: Number(cost.value) })
            .then(() => {
              title.value = '';
              cost.value = '';
              load();
            })
            .catch((err: Error) => {
              error.textContent = err.message;
            });
        },
      },
      el('div', { class: 'admin-form-fields' }, title, cost),
      el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('adminAdd'))),
    ),
  );
  load();
  return card;
}

function tagsCard(error: HTMLElement): HTMLElement {
  const list = el('div', {});
  const card = el('div', { class: 'card' }, el('h2', {}, t('adminTagsTitle')), list);

  function load(): void {
    api.get<{ tags: Tag[] }>('/api/tags').then(({ tags }) => {
      clear(list);
      for (const tg of tags) {
        const rules: string[] = [];
        if (tg.rules.spendable === false) rules.push(t('tagNotSpendable'));
        if (tg.rules.board) rules.push(t('tagBoard', { name: tg.rules.board }));
        list.append(
          el(
            'div',
            { class: 'task' },
            el(
              'div',
              {},
              el('span', { class: 'title' }, tg.name),
              el('span', { class: 'pill' }, t(tg.kind === 'category' ? 'tagKindCategory' : 'tagKindEligibility')),
              rules.length > 0 ? el('span', { class: 'meta' }, ` ${rules.join(', ')}`) : null,
            ),
          ),
        );
      }
    });
  }

  const name = textField('new-tag-name', t('adminTagPlaceholder'));
  const kind = selectField('new-tag-kind', t('adminTagKind'), [
    ['category', t('tagKindCategory')],
    ['eligibility', t('tagKindEligibility')],
  ]);
  const spendable = selectField('new-tag-spendable', t('adminTagSpending'), [
    ['yes', t('tagSpendable')],
    ['no', t('tagNotSpendable')],
  ]);
  const board = textField('new-tag-board', t('adminTagBoardPlaceholder'));

  card.append(
    el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          error.textContent = '';
          const rules: Record<string, unknown> = {};
          if (kind.value === 'category') {
            if (spendable.value === 'no') rules.spendable = false;
            if (board.value.trim()) rules.board = board.value.trim();
          }
          api
            .post('/api/tags', { name: name.value.trim(), kind: kind.value, rules })
            .then(() => {
              name.value = '';
              board.value = '';
              load();
            })
            .catch((err: Error) => {
              error.textContent = err.message;
            });
        },
      },
      el('div', { class: 'admin-form-fields' }, name, kind, spendable, board),
      el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('adminAdd'))),
      el(
        'p',
        { class: 'muted' },
        t('adminTagsHint'),
      ),
    ),
  );
  load();
  return card;
}

function reversalsCard(error: HTMLElement): HTMLElement {
  const list = el('div', {});
  const memberSelect = ce('md-outlined-select', { id: 'reversal-member', label: t('adminMemberLabel') }) as ValueEl;
  const card = el(
    'div',
    { class: 'card' },
    el('h2', {}, t('adminFixTitle')),
    el(
      'p',
      { class: 'muted' },
      t('adminFixHint'),
    ),
    memberSelect,
    list,
  );

  function loadMembers(): void {
    api.get<{ members?: Member[] }>('/api/state').then((state) => {
      clear(memberSelect);
      (state.members ?? []).forEach((m, i) => {
        memberSelect.append(ce('md-select-option', { value: String(m.id), ...(i === 0 ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, m.name)));
      });
      loadEarns();
    });
  }

  function loadEarns(): void {
    if (!memberSelect.value) return;
    api.get<{ entries: LedgerEntry[] }>(`/api/ledger?memberId=${memberSelect.value}`).then(({ entries }) => {
      clear(list);
      const correctable = entries
        .filter((e) => e.type === 'earn' || e.type === 'spend')
        .reverse()
        .slice(0, 15);
      if (correctable.length === 0) list.append(el('p', { class: 'muted' }, t('adminNothingToCorrect')));
      for (const e of correctable) {
        list.append(
          el(
            'div',
            { class: 'task' },
            el(
              'div',
              {},
              el('div', { class: 'title' }, e.note || t('adminEntryNumber', { id: e.id })),
              el('div', { class: 'meta' }, `${fmtMinutes(e.deltaMinutes)} · ${new Date(e.createdAt).toLocaleString(getLocale())}`),
            ),
            button('text', e.type === 'earn' ? t('adminReverse') : t('adminUndoSpend'), () => {
              error.textContent = '';
              api
                .post(`/api/ledger/${e.id}/reverse`, {
                  note: e.type === 'earn' ? t('adminReversedNote') : t('adminRefundedNote'),
                })
                .then(loadEarns)
                .catch((err: Error) => {
                  error.textContent = err.message;
                });
            }, { class: 'danger' }),
          ),
        );
      }
    });
  }

  memberSelect.addEventListener('change', loadEarns);
  loadMembers();
  return card;
}

function committedCard(error: HTMLElement): HTMLElement {
  const memberSelect = ce('md-outlined-select', { id: 'committed-member', label: t('adminMemberLabel') }) as ValueEl;
  const date = textField('committed-date', t('adminCommittedDate'), { type: 'date' });
  const minutes = textField('committed-minutes', t('adminCommittedMinutes'), { type: 'number', min: '1', 'suffix-text': t('composeMinSuffix') });
  const note = textField('committed-note', t('adminCommittedPlaceholder'));
  date.value = new Date().toISOString().slice(0, 10);

  api.get<{ members?: Member[] }>('/api/state').then((state) => {
    (state.members ?? []).forEach((m, i) => {
      memberSelect.append(ce('md-select-option', { value: String(m.id), ...(i === 0 ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, m.name)));
    });
  });

  return el(
    'div',
    { class: 'card' },
    el('h2', {}, t('adminCommittedTitle')),
    el('p', { class: 'muted' }, t('adminCommittedHint')),
    el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          error.textContent = '';
          api
            .post('/api/committed', {
              memberId: Number(memberSelect.value),
              date: date.value,
              minutes: Number(minutes.value),
              note: note.value.trim(),
            })
            .then(() => {
              minutes.value = '';
              note.value = '';
            })
            .catch((err: Error) => {
              error.textContent = err.message;
            });
        },
      },
      el('div', { class: 'admin-form-fields' }, memberSelect, date, minutes, note),
      el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit' }, t('adminLog'))),
    ),
  );
}
